// src/App.js
import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import Home from './pages/Home';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Configuration from './pages/Configuration';
import TrainingDetails from './pages/TrainingDetails';
import Trainings from './components/Trainings';
import AssignTraining from './components/TrainingAssign';
import CreateNewTrainingForm from './components/CreateNewTrainingForm'; // Import the form component
import ViewEditDeleteTrainings from './components/ViewEditDeleteTrainings'; // Import the new page component
import ParticipantTrainingView  from './components/ViewTrainings'; // Import the new page component
import TrainingParticipants from './components/TrainingParticipants'; // 
import TrainingAttendance from './components/TrainingAttendance';
import Layout from './components/Layout';
import PublicLayout from './components/PublicLayout';
import CourseConfiguration from './components/CourseConfiguration'; // Import the new page component
import CourseList from './pages/CourseList'; // Import the new page component
import UploadCourses from './components/CourseUpload';
import CompanyConfiguration from './components/CompanyConfiguration'; // Import the new page component
import DepartmentConfiguration from './components/DepartmentConfiguration'; // Import the new page component
import DepartmentAdminDashboard from './pages/DepartmentAdminDashboard';
import CompanyAdminDashboard from './pages/CompanyAdminDashboard';
import MasterAdminDashboard from './pages/MasterAdminDashboard';
import SuperAdminDashboard from './pages/SuperAdminDashboard';
import EmployeeDashboard from './pages/EmployeeDashboard';
import TrainerDashboard from './pages/TrainerDashboard';
import VendorAdminDashboard from './pages/VendorAdminDashboard';
import EmployeeDetails from './components/UserViewDetails';
import UserEdit from './components/UserEdit';
import Users from './components/Users';
import UserRegistrationForm from './components/UserRegistrationForm';
import UserManagementForm from './components/UserManagementForm';
import AssignUser from './components/UserAssign';
import UploadUsers from './components/UserUpload';
import CompanyList from './pages/ConfigCompany';
import DepartmentList from './pages/ConfigDepartment';
import QualificationConfiguration from './components/QualificationConfiguration';
import QualificationList from './pages/ConfigQualification';
import MailSettingsConfiguration from './components/MailSettingsConfiguration';
import RateLimitSettingsConfiguration from './components/RateLimitSettingsConfiguration';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import VerifyCertificate from './pages/VerifyCertificate';
import Reports from './pages/Reports';
import Library from './pages/Library';
import MessageBoard from './pages/MessageBoard';
import MessageForm from './components/MessageForm';
import TrainingCountSummary from './pages/reports/TrainingCountSummary';
import CancelledTrainingsReport from './pages/reports/CancelledTrainingsReport';
import CourseCompletionMatrix from './pages/reports/CourseCompletionMatrix';
import KIEligibilityMatrix from './pages/reports/KIEligibilityMatrix';
import AttendanceByDepartment from './pages/reports/AttendanceByDepartment';
import TrainingListAttendance from './pages/reports/TrainingListAttendance';
import './App.css';
import PrivateRoute from './components/PrivateRoute';
import Logout from './components/Logout';
import { getUser } from './utils/auth';
import 'bootstrap-icons/font/bootstrap-icons.css';

// App() itself never re-renders after the initial page load (it has no
// state/hooks tying it to route changes), so a role-based branch evaluated
// directly inline here — using getUser() at that single render — would get
// "frozen" at whatever role was logged in (usually none) when the app first
// loaded, and never update afterward on client-side navigate() calls
// post-login. Wrapping the branch in its own component defers the getUser()
// check to that component's own mount, which React Router does trigger
// fresh every time it mounts this route.
function AdminDashboardRouter() {
  // PrivateRoute above this component already restricts the route to
  // admin/masteradmin/companyadmin/departmentadmin, so 'admin' is the only
  // remaining possibility once the other three are ruled out.
  const role = getUser()?.role;
  if (role === 'departmentadmin') return <DepartmentAdminDashboard />;
  if (role === 'companyadmin') return <CompanyAdminDashboard />;
  if (role === 'masteradmin') return <MasterAdminDashboard />;
  return <SuperAdminDashboard />;
}

function App() {
  return (
    <Routes>
      <Route
          path="/"
          element={
            <PublicLayout>
              <Home />
            </PublicLayout>
          }
        />
        <Route
          path="/login"
          element={
            <PublicLayout>
              <Home /> {/* Since Home now includes the login form */}
            </PublicLayout>
          }
        />

        <Route path="/dashboard" element={<Layout><Dashboard /></Layout>} />
        <Route path="/configuration" element={<Layout><Configuration /></Layout>} />
        <Route path="/register" element={<PublicLayout><UserRegistrationForm /></PublicLayout>} />
        <Route path="/forgot-password" element={<PublicLayout><ForgotPassword /></PublicLayout>} />
        <Route path="/reset-password" element={<PublicLayout><ResetPassword /></PublicLayout>} />
        <Route path="/verify-certificate/:code" element={<PublicLayout><VerifyCertificate /></PublicLayout>} />
        <Route path="/create-user" element={<Layout><UserManagementForm /></Layout>} />

        <Route path="/employee/dashboard" element={<Layout><EmployeeDashboard /></Layout>} />
        <Route path="/my-trainings" element={<Layout><PrivateRoute roles={['masteradmin','companyadmin','departmentadmin','admin']}>
                <EmployeeDashboard />
              </PrivateRoute></Layout>} />
        <Route path="/trainer/dashboard" element={<Layout><PrivateRoute roles={['trainer']}>
                <TrainerDashboard />
              </PrivateRoute></Layout>} />
        <Route path="/vendor/dashboard" element={<Layout><PrivateRoute roles={['trainingadmin']}>
                <VendorAdminDashboard />
              </PrivateRoute></Layout>} />

        <Route path="/training/:id" element={<Layout><TrainingDetails /></Layout>} />
        <Route path="/training/view/:id" element={<PrivateRoute><Layout><ParticipantTrainingView /></Layout></PrivateRoute>} />
         {/* Define route for the Trainings list, viewable by all authenticated roles */}
         <Route path="/trainings" element={<Layout><PrivateRoute>
                <Trainings />
              </PrivateRoute></Layout>} />
        {/* Define route for creating new training */}
        <Route path="/training-provider/new-training" element={<Layout><CreateNewTrainingForm /></Layout>} />
        {/* Define route for View/Edit/Delete Trainings page */}
        <Route path="/training-provider/edit-training/:id" element={<PrivateRoute><Layout><ViewEditDeleteTrainings /></Layout></PrivateRoute>} />
        {/* Define route for Course/Training Configuration page */}
        <Route path="/training-provider/registered-participants/:id" element={<Layout><TrainingParticipants /></Layout>} />
        <Route path="/training-provider/configure-course" element={<Layout><CourseConfiguration /></Layout>} />
        <Route path="/training-provider/configure-course/:id" element={<Layout><CourseConfiguration /></Layout>} />
        <Route path="/training-provider/course-list" element={<Layout><CourseList /></Layout>} />
        <Route path="/training-provider/upload-course" element={<Layout><UploadCourses /></Layout>} />
        <Route path="/training-provider/assign-training/:id" element={<Layout><AssignTraining /></Layout>} />
        <Route path="/training-provider/attendance/:id" element={<Layout><TrainingAttendance /></Layout>} />
        {/* Define route for Training Provider Company Configuration page */}
        <Route path="/config/add-company" element={<Layout><CompanyConfiguration /></Layout>} />
        <Route path="/config/add-company/:id" element={<Layout><CompanyConfiguration /></Layout>} />
        <Route path="/config/company-list" element={<Layout><CompanyList /></Layout>} />
        <Route path="/config/department-list" element={<Layout><DepartmentList /></Layout>} />
        {/* Define route for Employee Company/Subsidiary and Department Configuration page */}
        <Route path="/config/company-department" element={<Layout><DepartmentConfiguration /></Layout>} />
        <Route path="/config/company-department/:id" element={<Layout><DepartmentConfiguration /></Layout>} />
        {/* Define route for Qualification/Role Configuration page */}
        <Route path="/config/add-qualification" element={<Layout><QualificationConfiguration /></Layout>} />
        <Route path="/config/add-qualification/:id" element={<Layout><QualificationConfiguration /></Layout>} />
        <Route path="/config/qualification-list" element={<Layout><QualificationList /></Layout>} />
        {/* Define route for Mail Settings Configuration page */}
        <Route path="/config/mail-settings" element={<Layout><MailSettingsConfiguration /></Layout>} />
        <Route path="/config/rate-limiting" element={<Layout><RateLimitSettingsConfiguration /></Layout>} />
        {/* Define route for Employee List page */}
        <Route path="/users" element={<Layout><Users /></Layout>} />
        {/* Define route for Employee Details page (view/edit) */}
        <Route path="/admin/employee/view/:id" element={<Layout><EmployeeDetails /></Layout>} />
        <Route path="/users/edit/:id" element={<Layout><UserEdit /></Layout>} />
        <Route path="/users/assign-training/:id" element={<Layout><AssignUser /></Layout>} />
        <Route path="/users/upload" element={<Layout><UploadUsers /></Layout>} />
        {/* Define route for Admin Dashboard */}
        <Route path="/admin/dashboard" element={<Layout><PrivateRoute roles={['admin','masteradmin','companyadmin','departmentadmin']}>
                <AdminDashboardRouter />
              </PrivateRoute></Layout>} />
        <Route path="/reports" element={<Layout><PrivateRoute roles={['masteradmin','admin','companyadmin','departmentadmin','trainer','trainingadmin']}>
                <Reports />
              </PrivateRoute></Layout>} />
        <Route path="/reports/training-count-summary" element={<Layout><PrivateRoute roles={['masteradmin','admin','companyadmin','departmentadmin']}>
                <TrainingCountSummary />
              </PrivateRoute></Layout>} />
        <Route path="/reports/cancelled-trainings" element={<Layout><PrivateRoute roles={['masteradmin','admin','companyadmin','departmentadmin']}>
                <CancelledTrainingsReport />
              </PrivateRoute></Layout>} />
        <Route path="/reports/course-completion-matrix" element={<Layout><PrivateRoute roles={['masteradmin','admin','companyadmin','departmentadmin']}>
                <CourseCompletionMatrix />
              </PrivateRoute></Layout>} />
        <Route path="/reports/ki-eligibility-matrix" element={<Layout><PrivateRoute roles={['masteradmin','admin','companyadmin','departmentadmin']}>
                <KIEligibilityMatrix />
              </PrivateRoute></Layout>} />
        <Route path="/reports/attendance-by-department" element={<Layout><PrivateRoute roles={['masteradmin','admin','companyadmin','departmentadmin']}>
                <AttendanceByDepartment />
              </PrivateRoute></Layout>} />
        <Route path="/reports/training-list-attendance" element={<Layout><PrivateRoute roles={['masteradmin','admin','companyadmin','departmentadmin','trainer','trainingadmin']}>
                <TrainingListAttendance />
              </PrivateRoute></Layout>} />
        <Route path="/library" element={<Layout><PrivateRoute>
                <Library />
              </PrivateRoute></Layout>} />
        <Route path="/messages" element={<Layout><PrivateRoute>
                <MessageBoard />
              </PrivateRoute></Layout>} />
        <Route path="/messages/new" element={<Layout><PrivateRoute>
                <MessageForm />
              </PrivateRoute></Layout>} />
        <Route path="/messages/edit/:id" element={<Layout><PrivateRoute>
                <MessageForm />
              </PrivateRoute></Layout>} />
        <Route path="/logout" element={<PublicLayout><Logout /></PublicLayout>} />
    </Routes>
  );
}

export default App;
